import React, { useEffect, useMemo, useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import useOrgJwt from '../hooks/useOrgJwt';
import './Settings.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
const DBG = process.env.NODE_ENV === 'development';
const L = (...args) => { if (DBG) console.log('[ACCOUNT]', ...args); };

// Plan display info (labels and features only - limits come from backend)
const PLAN_INFO = {
  free: {
    label: 'Free',
    price: '$0/mo',
    features: ['Core request/response logging', '30-day retention', 'Basic redaction']
  },
  pro: {
    label: 'Pro',
    price: '$199/mo',
    features: ['Advanced reports & exports', '90-day retention', 'GDPR/HIPAA/PCI rule packs']
  },
  enterprise: {
    label: 'Enterprise',
    price: '$499/mo',
    features: ['SSO & role policies', '365-day retention', 'Custom billing & SLAs']
  }
};

async function fetchBillingMe(orgJwt) {
  const r = await fetch(`${API_BASE}/billing/me`, {
    headers: { Authorization: `Bearer ${orgJwt}` },
    credentials: 'omit'
  });
  if (!r.ok) return null;
  const txt = await r.text();
  if (!txt) return null;
  try { return JSON.parse(txt); } catch { return null; }
}

async function openPortal(orgJwt, returnUrl) {
  const r = await fetch(`${API_BASE}/billing/create-portal-session`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${orgJwt}`, 'Content-Type': 'application/json' },
    credentials: 'omit',
    body: JSON.stringify({ ReturnUrl: returnUrl })
  });
  if (!r.ok) return null;
  const j = await r.json();
  return j.url || null;
}

async function listOrgUsers(orgJwt) {
  const r = await fetch(`${API_BASE}/organization-users`, {
    headers: { Authorization: `Bearer ${orgJwt}` },
    credentials: 'omit'
  });
  if (!r.ok) return [];
  const j = await r.json();
  return Array.isArray(j?.users) ? j.users : [];
}

async function removeUserFromOrg(orgJwt, clerkUserId) {
  const r = await fetch(`${API_BASE}/organization-users/${encodeURIComponent(clerkUserId)}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${orgJwt}` },
    credentials: 'omit'
  });
  return r.ok || r.status === 204;
}

function fmt(n) {
  if (n == null || isNaN(n)) return '0';
  return n.toLocaleString();
}

function formatTokens(n) {
  if (n == null || isNaN(n) || n === 0) return 'Unlimited';
  if (n >= 1000000) return `${(n / 1000000).toFixed(n % 1000000 === 0 ? 0 : 1)}M tokens / month`;
  if (n >= 1000) return `${(n / 1000).toFixed(0)}k tokens / month`;
  return `${n} tokens / month`;
}

export default function AccountSettings() {
  const { user } = useUser();
  const navigate = useNavigate();
  const { orgJwt, role, loading: jwtLoading } = useOrgJwt();

  const [state, setState] = useState({
    loading: true,
    tier: 'free',
    status: 'none',
    usage: null,
    tokenCap: 0,
    retentionDays: 30,
    error: '',
    deleting: false
  });

  const plan = useMemo(() => {
    const t = (state.tier || 'free').toLowerCase();
    return PLAN_INFO[t] || PLAN_INFO.free;
  }, [state.tier]);

  // Use backend token_cap instead of hardcoded limits
  const tokenLimit = state.tokenCap > 0 ? state.tokenCap : 250000; // fallback for free tier

  const usagePercent = useMemo(() => {
    const used = Number(state?.usage?.tokens_used || 0);
    const pct = Math.max(0, Math.min(100, Math.round((used / (tokenLimit || 1)) * 100)));
    return isFinite(pct) ? pct : 0;
  }, [state.usage, tokenLimit]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (jwtLoading) return;
      if (!orgJwt) {
        setState(s => ({ ...s, loading: false, error: 'No organization token. Join or create an organization first.' }));
        return;
      }
      try {
        const me = await fetchBillingMe(orgJwt);
        const sub = me?.subscription || {};
        const usage = me?.usage || null;
        if (!cancelled) {
          setState({
            loading: false,
            tier: (sub.tier || 'free').toLowerCase(),
            status: (sub.status || 'none').toLowerCase(),
            tokenCap: sub.token_cap || 0,
            retentionDays: sub.retention_days || 30,
            usage,
            error: '',
            deleting: false
          });
        }
      } catch (e) {
        if (!cancelled) setState(s => ({ ...s, loading: false, error: 'Failed to load billing.' }));
      }
    };
    run();
    return () => { cancelled = true; };
  }, [jwtLoading, orgJwt]);

  const isAdmin = role === 'admin';

  // Opens the Stripe billing portal for general management (invoices, payment methods)
  const handleOpenPortal = async () => {
    if (!orgJwt) return;
    const ret = window.location.href;
    const url = await openPortal(orgJwt, ret);
    if (url) window.location.href = url;
  };

  // Opens the portal with a message about managing/changing subscription
  const handleManageSubscription = () => {
    navigate('/billing/change-plan');
  };

  // Opens the portal for cancellation - Stripe portal handles actual cancellation
  const handleCancelSubscription = async () => {
    const confirmed = window.confirm(
      'This will open the billing portal where you can cancel your subscription. ' +
      'Your access will continue until the end of your current billing period. Continue?'
    );
    if (!confirmed) return;
    await handleOpenPortal();
  };

  const handleDelete = async () => {
    if (!user?.id || !orgJwt) return;
    
    if (!isAdmin) {
      const ok = window.confirm(
        'This will remove your account from this organization. ' +
        'Your Clerk user account will also be deleted. This cannot be undone. Continue?'
      );
      if (!ok) return;
      
      setState(s => ({ ...s, deleting: true }));
      try {
        const removed = await removeUserFromOrg(orgJwt, user.id);
        if (!removed) {
          setState(s => ({ ...s, deleting: false }));
          alert('Failed to remove account from organization.');
          return;
        }
        try { await user.delete(); } catch (e) { L('User delete failed:', e); }
        navigate('/goodbye');
      } catch (e) {
        L('Delete error:', e);
        setState(s => ({ ...s, deleting: false }));
        alert('Failed to delete account. Please try again.');
      }
      return;
    }
    
    // Admin flow - deleting the entire organization
    const ok1 = window.confirm(
      'WARNING: This will delete your account AND the entire organization. ' +
      'All members will be removed. This cannot be undone. Continue?'
    );
    if (!ok1) return;
    
    const ok2 = window.confirm(
      'Are you absolutely sure? This action is permanent and will remove all organization data.'
    );
    if (!ok2) return;
    
    setState(s => ({ ...s, deleting: true }));
    try {
      const members = await listOrgUsers(orgJwt);
      for (const m of members) {
        const uid = m?.clerk_user_id || m?.clerkUserId;
        if (uid && uid !== user.id) {
          try { await removeUserFromOrg(orgJwt, uid); } catch (e) { L('Failed to remove member:', uid, e); }
        }
      }
      // Remove self last
      try { await removeUserFromOrg(orgJwt, user.id); } catch (e) { L('Failed to remove self:', e); }
      try { await user.delete(); } catch (e) { L('User delete failed:', e); }
      navigate('/goodbye');
    } catch (e) {
      L('Delete error:', e);
      setState(s => ({ ...s, deleting: false }));
      alert('Failed to delete organization. Please try again.');
    }
  };

  if (state.loading || jwtLoading) {
    return <div className="p-6 text-zinc-400">Loading account…</div>;
  }

  if (state.error) {
    return (
      <div className="p-6">
        <div className="settings-card danger-card">{state.error}</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 settings-wrap">
      <div className="settings-header">
        <div>
          <div className="settings-kicker">Settings</div>
          <h1 className="text-2xl font-semibold text-zinc-100">Account & Billing</h1>
        </div>
        <div className="settings-badge">{isAdmin ? 'Admin' : 'Member'}</div>
      </div>

      <div className="settings-grid">
        <div className="settings-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="settings-kicker">Subscription</div>
              <h3>{plan.label}</h3>
              <div className="settings-subtle">{formatTokens(tokenLimit)}</div>
              <div className="mt-1 text-lg font-semibold">{plan.price}</div>
              <div className="feature-list">
                {plan.features.map(f => (
                  <div key={f} className="flex items-center gap-2">
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-cyan-400" />
                    <span className="text-sm">{f}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className={`status-pill ${state.status === 'active' || state.status === 'trialing' ? 'status-ok' : state.status === 'none' ? 'status-none' : 'status-warn'}`}>
                <span className="capitalize">{state.status}</span>
              </div>
            </div>
          </div>

          <div className="divider" />

          {isAdmin && (
            <div className="btn-row">
              <button onClick={handleManageSubscription} className="btn btn-primary" title="Upgrade, downgrade, or modify your plan">
                Change plan
              </button>
              {state.tier !== 'free' && (
                <button onClick={handleOpenPortal} className="btn btn-outline" title="View invoices and update payment methods">
                  Billing & invoices
                </button>
              )}
              {state.tier !== 'free' && (state.status === 'active' || state.status === 'trialing') && (
                <button onClick={handleCancelSubscription} className="btn btn-danger" title="Cancel your subscription in the billing portal">
                  Cancel subscription
                </button>
              )}
            </div>
          )}
          {!isAdmin && (
            <div className="settings-subtle" style={{ marginTop: '1rem' }}>
              Contact your organization admin to manage the subscription.
            </div>
          )}
        </div>

        <div className="settings-card">
          <div className="settings-kicker">Usage this period</div>
          <h3>Tokens</h3>
          <div className="kv">
            <span>Period</span>
            <span className="settings-subtle">{state.usage ? `${state.usage.period_start} → ${state.usage.period_end}` : 'n/a'}</span>
          </div>
          <div className="kv">
            <span>Used</span>
            <span>{fmt(Number(state?.usage?.tokens_used || 0))}</span>
          </div>
          <div className="kv">
            <span>Plan limit</span>
            <span>{fmt(tokenLimit)}</span>
          </div>
          <div className="meter mt-2">
            <div className="meter-fill" style={{ width: `${usagePercent}%` }} />
          </div>
          <div className="settings-subtle mt-1">{usagePercent}% of monthly allowance</div>
        </div>
      </div>

      <div className="settings-card danger-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="settings-kicker">Danger zone</div>
            <h3>{isAdmin ? 'Delete account and organization' : 'Delete account'}</h3>
            <p className="settings-subtle">
              {isAdmin ? 'Removes all members from the organization, then deletes your user.' : 'Removes your user from this organization and deletes your user.'}
            </p>
          </div>
          <button 
            onClick={handleDelete} 
            className="btn btn-danger" 
            disabled={state.deleting}
          >
            {state.deleting 
              ? 'Deleting...' 
              : (isAdmin ? 'Delete account and organization' : 'Delete account')
            }
          </button>
        </div>
      </div>
    </div>
  );
}
